import { makeAutoObservable, runInAction } from 'mobx'
import { Box, Icon, TooltipLink, Typography } from 'shared/ui'
import { LINK_COMPLIANCE_TOLL_FREE_LEARN_MORE_LINK } from 'shared/constants/links'
import { uiStore } from 'shared/store/uiStore'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { runBeforeAddNumber } from 'pages/settings/lib/runBeforeAddNumber'
import type { CompliancePageStore } from 'pages/settings/pages/compliance/store/compliancePageStore'
import type { IRegisterSectionProps } from 'pages/settings/pages/compliance/ui/RegisterSection/RegisterSection'
import styles from 'pages/settings/pages/compliance/ui/styles.module.scss'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { TollFreeRoutesSteps } from 'pages/settings/pages/compliance/pages/tollFree/route/type'

export class TollFreePageStore {
  private _loading = false

  constructor(
    private _compliancePageStore: CompliancePageStore,
    private _entities: IEntitiesStore
  ) {
    makeAutoObservable(this)
  }

  init = async () => {
    runInAction(() => {
      this._loading = true
    })

    await Promise.all([window.publicApiVue?.fetchNumbers()])

    runInAction(() => {
      this._loading = false
    })
  }

  get disabled() {
    return this._loading
  }

  get businessProfile() {
    return this._compliancePageStore.complianceLayerStore.organizationBusinessProfile
      ?.businessProfile
  }

  get complianceLayerStore() {
    return this._compliancePageStore.complianceLayerStore
  }

  get organizationTollFree() {
    return this._compliancePageStore.complianceLayerStore.organizationTollFree
  }

  get tollFreeProfileItems() {
    if (!this.organizationTollFree) return []

    return this.organizationTollFree.items
  }

  get isTollFreeDataExist() {
    return Boolean(this.tollFreeProfileItems.length)
  }

  get tollFreeDataStatus() {
    if (this.complianceLayerStore.tollFreeUnverifiedNumbers.length > 0) return 'alert'
    if (this.tollFreeProfileItems.length === 0) return

    if (this.organizationTollFree?.isStatusBlocked) return 'failed'
    if (this.organizationTollFree?.isStatusRejected) return 'failed'
    if (this.organizationTollFree?.isStatusPendingReview) return 'alert'
    if (this.organizationTollFree?.isStatusInCarrierReview) return 'alert'
    if (this.organizationTollFree?.isStatusInUnderReview) return 'under_review'
    if (this.organizationTollFree?.isStatusVerified) return 'approved'

    return
  }

  get compliancePageStore() {
    return this._compliancePageStore
  }

  get tfRegisterSectionProps(): IRegisterSectionProps {
    return {
      title: 'Toll-free verification',
      titleType: 'body-lg-medium',
      desc: (
        <Typography
          ariaLabel='Toll-free verification_desc'
          variant='body-md-regular'
          tag='div'
          className={styles.tollFreeVerificationDesc}
        >
          Required for sending SMS <Icon icon='arrowRight' className={styles.arrow} /> to
          <Icon className={styles.flag} icon='flagUSA' />
          US and <Icon className={styles.flag} icon='flagCanada' /> Canada.
        </Typography>
      ),
      learnMoreLink: LINK_COMPLIANCE_TOLL_FREE_LEARN_MORE_LINK,
      actionButton: {
        tooltipProps: this._entities.organizationStore.isNumbersGracePeriod
          ? {
              type: 'description',
              label: (
                <Box display='flex' flexDirection='column' gap={0.5}>
                  <Typography
                    variant={'body-md-regular'}
                    color={'var(--content-primary-primary-inverted)'}
                    ariaLabel={'Grace Period'}
                  >
                    Take care of unpaid licenses
                    <br /> before adding new numbers.
                  </Typography>
                </Box>
              ),
              width: 233,
              placement: 'top',
              desc: (
                <TooltipLink
                  text='Manage numbers'
                  onClick={() => {
                    uiStore.changeRoute({
                      path: '/settings/organization/numbers',
                    })
                  }}
                />
              ),
            }
          : undefined,
        text: this.isTollFreeDataExist ? 'Add use case' : 'Register',
        disabled: this.disabled || this._entities.organizationStore.isNumbersGracePeriod,
        onClick: () => {
          if (this._entities.usersStore.user?.isRequireNumber) {
            this._entities.numbersStore.initSelectNumberModal()
            return
          }

          if (!this._entities.featureFlagsStore.salesmsg_design_v2_compliance_toll_free) {
            window.publicApiVue?.onCheckToolFreeNumbers()

            return
          }

          const { tollFreeStepsStore } = this.compliancePageStore
          const { tollFreeUnverifiedNumbers } = this.complianceLayerStore

          if (!tollFreeUnverifiedNumbers.length) {
            runBeforeAddNumber(this._entities, () =>
              tollFreeStepsStore.addTollFreeNumberStore.openAddNumberModal()
            )
          } else {
            uiStore.changeRoute({
              path: `/${SettingsPath.compliance.tollFree.steps}/${TollFreeRoutesSteps.numbers}`,
            })
          }
        },
        ...(this.isTollFreeDataExist
          ? {
              contained: 'secondary',
              icon: 'add',
            }
          : null),
      },
      isSecondStep: this.isTollFreeDataExist,
    }
  }
}
